import type { ActingFor, ActForOffer } from '@myrewards/contracts';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { meQuery } from '../api';
import { ActForStep, type ActForDone } from '../acting-for/ActForStep';
import { ForWhom, type ForWhomValue } from '../acting-for/ForWhom';
import { useExecutivesToName } from '../acting-for/useActForPeople';
import { firstName, recordedLine, withNoteAndFiles } from '../acting-for/words';
import { formatCount, formatMoney, TextField } from '../ui';
import type { BudgetValue, LedgerWrite } from './budget-model';
import { LEDGER_KIND_LABEL } from './model';

// APP-148 D20 (Budget board): a budget change a Stock Admin makes can be marked "For an
// Executive". It's a marker, never new authority: the same change, named for the Executive who
// asked, who is told. The step opens in the row, under "Who is this change for?".

/** The Executives a Stock Admin may name for a budget change, and who's acting; null otherwise. */
export function useBudgetActFor(): { offer: ActForOffer; actorName: string } | null {
  const { data: me } = useSuspenseQuery(meQuery);
  const offer = useExecutivesToName();
  return offer === null ? null : { offer, actorName: me.person.displayName };
}

/** "Who is this change for? Just me / For an Executive". */
export function BudgetForWhom({
  value,
  onChange,
}: {
  value: ForWhomValue;
  onChange: (value: ForWhomValue) => void;
}): ReactElement {
  return (
    <ForWhom
      label="Who is this change for?"
      someone="For an Executive"
      value={value}
      onChange={onChange}
    />
  );
}

/** "$25,000", "500 options", "$25,000 and 500 options". */
function amountWords(cashCents: number, options: number): string {
  return [
    cashCents > 0 ? formatMoney(cashCents) : null,
    options > 0 ? `${formatCount(options)} options` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' and ');
}

/** A ledger write as the board's line says it: "Addition, +$25,000", "Reduction, −956 options". */
function writeWords(w: LedgerWrite): string {
  const sign = w.kind === 'initial' ? '' : w.kind === 'reduction' ? '−' : '+';
  const parts = [
    w.cashCents > 0 ? `${sign}${formatMoney(w.cashCents)}` : null,
    w.options > 0 ? `${sign}${formatCount(w.options)} options` : null,
  ].filter((part): part is string => part !== null);
  return `${LEDGER_KIND_LABEL[w.kind]}, ${parts.join(' and ')}`;
}

/** Cash after a change, over budget in words, never a bare minus (QA-036). */
function cashWords(cents: number): string {
  return cents < 0 ? `${formatMoney(-cents)} over budget` : formatMoney(cents);
}

/** What a pool's edit does, as "Check before saving" lists it (Budget board). */
export function poolChange(
  pool: { name: string; budget: BudgetValue | null; remaining: BudgetValue | null },
  fiscalYear: number,
  after: BudgetValue | null,
  writes: readonly LedgerWrite[],
): { lines: string[]; action: string; verb: string } {
  const year = String(fiscalYear);
  if (after === null || writes.length === 0)
    return {
      lines: ['Change the cash or options budget to see the ledger entry it makes.'],
      action: 'Change the budget',
      verb: 'Changed',
    };
  const before = pool.budget;
  const lines: string[] = [];
  if (before === null || before.cashCents !== after.cashCents)
    lines.push(
      `${pool.name} · cash budget ${year}: ${before === null ? '' : `${formatMoney(before.cashCents)} → `}${formatMoney(after.cashCents)}`,
    );
  if (before === null || before.options !== after.options)
    lines.push(
      `${pool.name} · options budget ${year}: ${before === null ? '' : `${formatCount(before.options)} → `}${formatCount(after.options)}`,
    );
  if (before !== null && pool.remaining !== null && before.cashCents !== after.cashCents) {
    const left = pool.remaining.cashCents;
    lines.push(
      `${pool.name} · cash left ${year}: ${cashWords(left)} → ${cashWords(left + after.cashCents - before.cashCents)}`,
    );
  }
  lines.push(
    `${writes.length === 1 ? 'Ledger entry' : 'Ledger entries'} for ${year}: ${writes.map(writeWords).join('; ')}, with the reason above.`,
  );
  const [only] = writes;
  if (writes.length > 1 || only === undefined)
    return { lines, action: 'Change the budget', verb: 'Changed' };
  if (only.kind === 'initial') return { lines, action: 'Set the budget', verb: 'Set' };
  const amount = amountWords(only.cashCents, only.options);
  return only.kind === 'addition'
    ? { lines, action: `Add ${amount}`, verb: 'Added' }
    : { lines, action: `Reduce by ${amount}`, verb: 'Reduced' };
}

/** What a move does: both pools' budgets before → after, and an entry on each. */
export function moveChange(
  from: { name: string; budget: BudgetValue | null } | undefined,
  to: { name: string; budget: BudgetValue | null } | undefined,
  fiscalYear: number,
  amount: BudgetValue,
): { lines: string[]; action: string; verb: string } {
  const year = String(fiscalYear);
  const moving = amountWords(amount.cashCents, amount.options);
  if (from?.budget == null || to?.budget == null || moving === '')
    return {
      lines: ['Choose the two pools and how much to move.'],
      action: 'Move budget',
      verb: 'Moved',
    };
  const lines: string[] = [];
  for (const [pool, sign] of [
    [from, -1],
    [to, 1],
  ] as const) {
    const b = pool.budget;
    if (b === null) continue;
    if (amount.cashCents > 0)
      lines.push(
        `${pool.name} · cash budget ${year}: ${formatMoney(b.cashCents)} → ${formatMoney(b.cashCents + sign * amount.cashCents)}`,
      );
    if (amount.options > 0)
      lines.push(
        `${pool.name} · options budget ${year}: ${formatCount(b.options)} → ${formatCount(b.options + sign * amount.options)}`,
      );
  }
  lines.push(`Ledger entries for ${year}: a Move on each pool, with the reason above.`);
  return { lines, action: `Move ${moving}`, verb: 'Moved' };
}

/**
 * The step for a budget change done for an Executive, in the row (Budget board): the Executive
 * (required), why (the ledger's reason), files and a note, "Check before saving", "Recorded as",
 * and the change named for the person ("Add $25,000 for Marcus").
 */
export function BudgetStep({
  offer,
  actorName,
  subtitle,
  change,
  ready,
  onConfirm,
  onCancel,
}: {
  offer: ActForOffer;
  actorName: string;
  /** "Maritime Airborne · cash budget 2026". */
  subtitle: string;
  change: { lines: readonly string[]; action: string; verb: string };
  /** The change itself is complete (a figure changed, the pools chosen). */
  ready: boolean;
  /** Saves it for the person named, with the reason; throw to keep the step open. */
  onConfirm: (reason: string, actingFor: ActingFor, done: ActForDone) => Promise<void>;
  onCancel: () => void;
}): ReactElement {
  const [reason, setReason] = useState('');
  const nameOf = (personId: string | null): string | null => {
    const person = offer.people.find((p) => p.id === personId);
    return person === undefined ? null : firstName(person.name);
  };
  return (
    <ActForStep
      inline
      title="Change for an Executive"
      subtitle={subtitle}
      intro="You’re making this change for an Executive. It’s recorded as your change, done for them, and they’re told."
      actorName={actorName}
      offer={offer}
      firstNameOf={(id) => nameOf(id) ?? ''}
      whoLabel="Executive"
      whoHint="Name the Executive who asked. They’re told, and the ledger says it was done for them."
      noteHint={(name) =>
        `The reason above goes in the ledger. A note is kept with the change and shown to ${name.startsWith('The ') ? `the ${name.slice(4)}` : name}.`
      }
      summaryTitle="Check before saving"
      summary={(personId, added) => (
        <>
          {change.lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
          <li>
            {`${nameOf(personId) ?? 'The Executive you name'} gets a notice in the bell and by email${withNoteAndFiles(added.note, added.files)}.`}
          </li>
        </>
      )}
      recorded={(person) => recordedLine(change.verb, actorName, person, 'executive')}
      confirmLabel={(person) => `${change.action} for ${firstName(person.name)}`}
      unnamedLabel={change.action}
      ifNotWanted={null}
      ready={ready && reason.trim() !== ''}
      onConfirm={(actingFor, done) => onConfirm(reason.trim(), actingFor, done)}
      onCancel={onCancel}
    >
      <TextField
        label="Why"
        multiline
        value={reason}
        onChange={setReason}
        isRequired
        description="It goes in the ledger with the change."
      />
    </ActForStep>
  );
}
